import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Chip, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Skeleton } from '@mui/material';
import {
    Business as BusinessIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Error as ErrorIcon,
    TrendingUp as TrendingUpIcon,
    Add as AddIcon,
    Settings as SettingsIcon,
    Refresh as RefreshIcon,
    Circle as CircleIcon,
} from '@mui/icons-material';

interface TenantStats {
    total: number;
    active: number;
    inactive: number;
    error: number;
    pending: number;
}

interface SystemHealth {
    status: 'healthy' | 'warning' | 'error';
    uptime: string;
    lastUpdated: string;
    services: {
        api: 'healthy' | 'warning' | 'error';
        database: 'healthy' | 'warning' | 'error';
        auth: 'healthy' | 'warning' | 'error';
    };
}

export function Dashboard() {
    const navigate = useNavigate();
    const [tenantStats, setTenantStats] = useState<TenantStats>({
        total: 0,
        active: 0,
        inactive: 0,
        error: 0,
        pending: 0,
    });

    const [systemHealth, setSystemHealth] = useState<SystemHealth>({
        status: 'healthy',
        uptime: '99.9%',
        lastUpdated: new Date().toISOString(),
        services: {
            api: 'healthy',
            database: 'healthy',
            auth: 'healthy',
        },
    });

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadDashboardData = async () => {
            try {
                await new Promise((resolve) => setTimeout(resolve, 1000));

                setTenantStats({
                    total: 42,
                    active: 38,
                    inactive: 2,
                    error: 1,
                    pending: 1,
                });

                setSystemHealth({
                    status: 'healthy',
                    uptime: '99.9%',
                    lastUpdated: new Date().toISOString(),
                    services: {
                        api: 'healthy',
                        database: 'healthy',
                        auth: 'healthy',
                    },
                });
            } catch (error) {
                console.error('Failed to load dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadDashboardData();
    }, []);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'healthy':
                return 'success';
            case 'warning':
                return 'warning';
            case 'error':
                return 'error';
            default:
                return 'default';
        }
    };

    const statsCards = [
        {
            title: 'Total Tenants',
            value: tenantStats.total,
            icon: BusinessIcon,
            color: 'primary.main',
            description: 'All registered tenants',
        },
        {
            title: 'Active Tenants',
            value: tenantStats.active,
            icon: CheckCircleIcon,
            color: 'success.main',
            description: 'Currently online',
        },
        {
            title: 'Pending',
            value: tenantStats.pending,
            icon: ScheduleIcon,
            color: 'warning.main',
            description: 'Awaiting approval',
        },
        {
            title: 'Issues',
            value: tenantStats.error,
            icon: ErrorIcon,
            color: 'error.main',
            description: 'Needs attention',
        },
    ];

    if (loading) {
        return (
            <Box>
                <Box sx={{ mb: 4 }}>
                    <Skeleton variant="text" width={200} height={48} />
                    <Skeleton variant="text" width={400} height={24} />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
                    {[...Array(4)].map((_, i) => (
                        <Card key={i}>
                            <CardContent>
                                <Skeleton variant="circular" width={40} height={40} />
                                <Skeleton variant="text" width="60%" />
                                <Skeleton variant="text" width="40%" height={32} />
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h1" gutterBottom>
                    Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Welcome to the Torvia Tenant Management Portal
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
                    <Chip icon={<CircleIcon sx={{ fontSize: 12, color: 'success.main' }} />} label="System Online" size="small" variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                        Last updated: {new Date().toLocaleTimeString()}
                    </Typography>
                </Box>
            </Box>

            {/* Statistics Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 4 }}>
                {statsCards.map((card, index) => {
                    const Icon = card.icon;
                    return (
                        <Card key={index} sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Icon sx={{ color: card.color, mr: 2 }} />
                                    <Typography variant="h6" component="div">
                                        {card.title}
                                    </Typography>
                                </Box>
                                <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                                    {card.value}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {card.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {/* Main Content Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                {/* System Health */}
                <Card sx={{ height: 'fit-content' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h5" component="div">
                                System Health
                            </Typography>
                            <Chip
                                label={systemHealth.status}
                                color={getStatusColor(systemHealth.status) as 'success' | 'warning' | 'error' | 'default'}
                                size="small"
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                System Uptime
                            </Typography>
                            <Typography variant="h6">{systemHealth.uptime}</Typography>
                            <LinearProgress variant="determinate" value={99.9} sx={{ mt: 1, height: 8, borderRadius: 4 }} />
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                            Service Status
                        </Typography>
                        <List dense>
                            {Object.entries(systemHealth.services).map(([service, status]) => (
                                <ListItem key={service} sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <CircleIcon
                                            sx={{
                                                fontSize: 12,
                                                color: status === 'healthy' ? 'success.main' : status === 'warning' ? 'warning.main' : 'error.main',
                                            }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={`${service.charAt(0).toUpperCase() + service.slice(1)} Service`} secondary={status} />
                                </ListItem>
                            ))}
                        </List>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            Last updated: {new Date(systemHealth.lastUpdated).toLocaleString()}
                        </Typography>
                    </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card sx={{ height: 'fit-content' }}>
                    <CardContent>
                        <Typography variant="h5" component="div" sx={{ mb: 3 }}>
                            Quick Actions
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<BusinessIcon />}
                                onClick={() => navigate('/tenants')}
                                fullWidth
                                sx={{ justifyContent: 'flex-start', py: 1.5 }}
                            >
                                <Box sx={{ textAlign: 'left', ml: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        View All Tenants
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Manage tenant accounts
                                    </Typography>
                                </Box>
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => alert('Create Tenant functionality coming soon!')}
                                fullWidth
                                sx={{ justifyContent: 'flex-start', py: 1.5 }}
                            >
                                <Box sx={{ textAlign: 'left', ml: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        Create New Tenant
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Add a new tenant account
                                    </Typography>
                                </Box>
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<SettingsIcon />}
                                onClick={() => navigate('/settings')}
                                fullWidth
                                sx={{ justifyContent: 'flex-start', py: 1.5 }}
                            >
                                <Box sx={{ textAlign: 'left', ml: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        System Settings
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Configure application
                                    </Typography>
                                </Box>
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={() => window.location.reload()}
                                fullWidth
                                sx={{ justifyContent: 'flex-start', py: 1.5 }}
                            >
                                <Box sx={{ textAlign: 'left', ml: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        Refresh Dashboard
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Update all data
                                    </Typography>
                                </Box>
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Recent Activity */}
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Typography variant="h5" component="div">
                            Recent Activity
                        </Typography>
                        <Button size="small">View All</Button>
                    </Box>

                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <TrendingUpIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No Recent Activity
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Activity logs and tenant management events will appear here once you start managing tenants.
                        </Typography>
                        <Button variant="contained" startIcon={<BusinessIcon />} onClick={() => navigate('/tenants')}>
                            Get Started
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
